import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  MenuItem,
  Select,
  TextField,
  Typography,
  Paper,
  Grid,
  InputLabel,
  FormControl,
  Alert,
  Snackbar,
  Divider,
  InputAdornment,
} from "@mui/material";
import {
  EventNote,
  Route as RouteIcon,
  Title,
  Save,
  RestartAlt,
  WbTwilight,
  LightMode,
  WbSunny,
  NightsStay,
} from "@mui/icons-material";
import { AdminPage, SectionTitle, GREEN, primaryButtonSx, subCardSx } from "../admin/AdminPageLayout";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { create_travel_timeline, travel_admin } from "../api/API";

function MainAddRouteDetails() {
  const [route, setRoute] = useState("");
  const [dates, setDates] = useState("");
  const [sections, setSections] = useState({
    morningDetails: "",
    middayDetails: "",
    afternoonDetails: "",
    eveningDetails: "",
  });
  const [loading, setLoading] = useState(false);
  const [travelses, setTravelses] = useState([]); // 👉 ลิสต์ข้อมูลจาก API
  const token = localStorage.getItem("token");
  const hasFetched = useRef(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // "success" | "error" | "warning" | "info"

  const loadTravelses = async () => {
    try {
      const response = await axios.get(travel_admin, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      // console.log("response", response?.data);

      setTravelses(response.data || []); // ตรวจสอบรูปแบบ data ด้วย (array?)
      console.log("✅ ดึงข้อมูลอำเภอสำเร็จ:", response.data);
    } catch (error) {
      console.error("❌ ดึงข้อมูลอำเภอล้มเหลว:", error);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  useEffect(() => {
    if (!hasFetched.current && token) {
      loadTravelses();
      hasFetched.current = true;
    }
  }, []);

  const handleChange = (field) => (event) => {
    setSections((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleReset = () => {
    setRoute("");
    setDates("");
    setSections({
      morningDetails: "",
      middayDetails: "",
      afternoonDetails: "",
      eveningDetails: "",
    });
    // console.log("Form reset");
  };

  const handleSubmit = async () => {
    const payload = {
      route,
      dates,
      ...sections,
    };
    // console.log("Submitted Data:", payload);
    setLoading(true); // 🔄 เริ่มโหลด

    try {
      const decoded = jwtDecode(token);
      const uid = decoded?.uid || decoded?.user_id || null;

      const payload = {
        ...sections,
        uid: parseInt(uid),
        tid: route,
        title: dates
        // name, // ✅ ใช้ค่าจาก localStorage
      };

      // console.log(" Payload พร้อมส่ง:", payload);

      const response = await axios.post(
        create_travel_timeline,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        setSnackbarMessage("✅ ข้อมูลถูกบันทึกเรียบร้อยแล้ว!");
        setSnackbarSeverity("success");
        // เคลียร์ค่าฟอร์ม
        // setRoute("");
        setDates("");
        setSections({
          morningDetails: "",
          middayDetails: "",
          afternoonDetails: "",
          eveningDetails: "",
        });
      } else {
        setSnackbarMessage("❌ ไม่สามารถบันทึกข้อมูลได้");
        setSnackbarSeverity("error");
      }
    } catch (error) {
      console.error("❗ Error ส่งข้อมูล:", error);
      setSnackbarMessage("⚠️ เกิดข้อผิดพลาดขณะส่งข้อมูล");
      setSnackbarSeverity("error");
    } finally {
      setSnackbarOpen(true);
      setLoading(false); // 🔚 หยุดโหลด
    }
  };

  return (
    <AdminPage
      icon={<EventNote fontSize="large" />}
      title="เพิ่มรายละเอียด Route"
      subtitle="กำหนดกำหนดการของเส้นทางแยกตามช่วงเวลา เช้า กลางวัน บ่าย เย็น"
      actions={
        <>
          <Button
            variant="outlined"
            size="large"
            onClick={handleReset}
            startIcon={<RestartAlt />}
            sx={{ borderRadius: 3, width: { xs: "100%", sm: "auto" }, borderColor: "grey.400", color: "text.secondary" }}
          >
            รีเซตค่า
          </Button>
          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            startIcon={<Save />}
            sx={primaryButtonSx}
          >
            ยืนยันการเพิ่ม
          </Button>
        </>
      }
    >
      <SectionTitle step={1} title="เลือกเส้นทางและหัวข้อ" />
      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel shrink>เส้นทาง</InputLabel>
            <Select
              value={route}
              onChange={(e) => setRoute(e.target.value)}
              displayEmpty
              notched
              label="เส้นทาง"
              startAdornment={
                <InputAdornment position="start">
                  <RouteIcon sx={{ color: GREEN }} />
                </InputAdornment>
              }
            >
              <MenuItem value="">
                <em>-- เลือกเส้นทาง --</em>
              </MenuItem>
              {travelses?.map((d) => (
                <MenuItem key={d.tid} value={d.tid}>
                  {d.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="หัวข้อ"
            placeholder="วันที่ 1, วันที่ 2,..."
            fullWidth
            value={dates}
            onChange={(e) => setDates(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Title sx={{ color: GREEN }} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <SectionTitle step={2} title="รายละเอียดแต่ละช่วงเวลา" />
      <Grid container spacing={2}>
        {[
          { label: "เช้า", key: "morningDetails", icon: <WbTwilight /> },
          { label: "กลางวัน", key: "middayDetails", icon: <LightMode /> },
          { label: "บ่าย", key: "afternoonDetails", icon: <WbSunny /> },
          { label: "เย็น", key: "eveningDetails", icon: <NightsStay /> },
        ].map(({ label, key, icon }) => (
          <Grid item xs={12} md={6} key={key}>
            <Paper variant="outlined" sx={{ ...subCardSx, height: "100%" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, color: GREEN }}>
                {icon}
                <Typography fontWeight={700}>{label}</Typography>
              </Box>
              <TextField
                multiline
                rows={3}
                placeholder="รายละเอียด..."
                value={sections[key]}
                onChange={handleChange(key)}
                fullWidth
                sx={{ bgcolor: "white" }}
              />
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </AdminPage>
  );
}

export default MainAddRouteDetails;
